import {Collection} from '@tinct/web-collection';
import {useCampaignsListColumns} from './hooks/use-campaigns-list-columns';
import {useGetCampaigns} from '@tinct/core-api';
import {LayoutContent, LayoutHeader, LayoutTopbar, useBreadcrumb} from '@tinct/web-layout';
import {useCurrentOrganization, useCurrentWorkspaceUrlPrefix, useGetCurrentWorkspaceId} from '@tinct/core-workspace';
import {Button, Tooltip, TooltipContent, TooltipTrigger} from '@tinct/web-components';
import {MegaphoneIcon, SquarePen} from 'lucide-react';
import {Link, useNavigate} from 'react-router-dom';
import {useStaticAssetsUrl} from '@tinct/core-utils';
import {NEW_CAMPAIGN_DIALOG_HASH} from '../../modals/new-campaign-dialog/new-campaign-dialog';

function CampaignsEmptyState() {
  const organization = useCurrentOrganization();

  return (
    <div className="flex flex-col items-center justify-center py-16">
      <div className="flex items-center justify-center size-10 rounded-lg bg-muted mb-4">
        <MegaphoneIcon className="size-6" />
      </div>
      <p className="text-lg font-medium text-foreground">No campaign yet</p>
      <p className="text-sm text-muted-foreground mb-6">
        Create your first campaign
      </p>
      <Tooltip>
        <TooltipTrigger asChild>
          <span>
            <Button disabled={organization?.lastBrandVersion == null} asChild={organization?.lastBrandVersion != null}>
              <Link to={{hash: NEW_CAMPAIGN_DIALOG_HASH}} className={'flex items-center justify-center gap-2 m-0 p-0'}>
                <SquarePen className="size-4" />
                Create a campaign
              </Link>
            </Button>
          </span>
        </TooltipTrigger>
        {organization?.lastBrandVersion == null && (
          <TooltipContent>
            Complete your brand analysis first
          </TooltipContent>
        )}
      </Tooltip>
    </div>
  );
}

export function ListCampaignsPage() {
  const workspaceId = useGetCurrentWorkspaceId();
  const workspaceBaseUrl = useCurrentWorkspaceUrlPrefix();
  const organization = useCurrentOrganization();

  const columns = useCampaignsListColumns({ basePath: workspaceBaseUrl });
  const navigate = useNavigate();
  const assetsUrl = useStaticAssetsUrl();
  const breadcrumbs = useBreadcrumb([
    { label: 'Dashboard', to: `${workspaceBaseUrl}/dashboard` },
  ], true);

  return (
    <div>
      <header className="sticky top-0 z-30">
        <LayoutTopbar
          breadcrumbs={breadcrumbs}
        />
      </header>
      <LayoutContent>
        <div className="flex w-full justify-between items-end">
          <LayoutHeader
            pageTitle="Campaigns"
            pageDescription="Manage campaigns and track performances."
          />
          <img
            src={`${assetsUrl}/campaigns-header-illustration.png`}
            alt=""
            className="h-[120px] w-[360px] shrink-0 object-cover pointer-events-none"
          />
        </div>
        <Collection
          useLocationAsState
          defaultSorting={[{ id: 'updatedAt', desc: true }]}
          columns={columns}
          onRowClick={(campaign) => campaign.id && navigate(`${workspaceBaseUrl}/campaigns/${campaign.id}`)}
          filtersConfiguration={{
            textFilter: {
              placeholder: 'Search campaigns...',
              queryParamName: 'term',
            },
          }}
          toolbarActions={
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button disabled={organization?.lastBrandVersion == null} asChild={organization?.lastBrandVersion != null}>
                    <Link to={{hash: NEW_CAMPAIGN_DIALOG_HASH}} className={'flex items-center justify-center gap-2 m-0 p-0'}>
                      <SquarePen className="size-4" />
                      New campaign
                    </Link>
                  </Button>
                </span>
              </TooltipTrigger>
              {organization?.lastBrandVersion == null && (
                <TooltipContent>
                  Complete your brand analysis first
                </TooltipContent>
              )}
            </Tooltip>
          }
          useQueryFn={useGetCampaigns}
          staticParams={{
            organizationId: workspaceId,
          }}
          queryFields={{
            id: null,
            name: null,
            status: null,
            landingPageVariationAxis: null,
            audience: {
              fields: {
                id: null,
                name: null,
              },
            },
            pages: {
              fields: {
                id: null,
              },
            },
            statistics: {
              fields: {
                visitsCount: null,
                publishedPagesCount: null,
                totalPagesCount: null,
                leadsCount: null,
                // Gates the kebab's Delete action.
                eventsCount: null,
              },
            },
            createdAt: null,
            updatedAt: null,
          }}
          emptyState={<CampaignsEmptyState />}
        />
      </LayoutContent>
    </div>
  );
}
